import { remiApi } from "./remiApi";
import {
  SavedProperty,
  SavedPropertyResponse,
} from "../types/savedProperty";

// Shape returned by GET /api/v1/idx/saved-properties. The endpoint is scoped to
// the signed-in web user's own contact (Firebase token), so no contact/org args
// travel over the wire — the callers still pass them, but only to shape the
// returned `SavedProperty` objects.
interface IdxSavedProperty {
  property_id: string;
  mls_number: string | null;
  price: number | null;
  address: { full?: string; city?: string; state?: string; zip?: string } | null;
  primary_photo_url: string | null;
  saved_at: string | null;
  last_viewed_at: string | null;
}

const EMPTY_ADDRESS = { full: "", city: "", state: "", zip: "" };

const toSavedProperty = (
  raw: IdxSavedProperty,
  contactId: string,
  organizationId: string
): SavedProperty => ({
  // The property id is the stable key the IDX uses everywhere (save, unsave,
  // navigation), so it doubles as `id` — the DELETE endpoint keys on it.
  id: raw.property_id,
  contactId,
  organizationId,
  propertyId: raw.property_id,
  mlsNumber: raw.mls_number ?? undefined,
  address: { ...EMPTY_ADDRESS, ...(raw.address ?? {}) },
  price: raw.price ?? undefined,
  primaryPhotoUrl: raw.primary_photo_url ?? undefined,
  savedAt: raw.saved_at ?? "",
  lastViewedAt: raw.last_viewed_at ?? undefined,
});

// Shared in-flight promise for the saved-properties list, so concurrent callers
// (all the property cards on a page) share one network request.
let inFlightSavedList: Promise<SavedProperty[]> | null = null;

export const savedPropertyService = {
  /**
   * Save a property for the signed-in web user. The POST is idempotent
   * (insert-or-update on the contact + property), so there's no find-first
   * round-trip — repeat saves just refresh `last_viewed_at`.
   */
  async saveProperty(
    _contactId: string,
    _organizationId: string,
    propertyId: string,
    mlsNumber: string | undefined,
    mlsBoardCode: string | undefined,
    address: {
      full: string;
      city: string;
      state: string;
      zip: string;
    },
    details?: {
      bedrooms?: number;
      bathrooms?: number;
      squareFeet?: number;
      price?: number;
      listprice?: string;
      listpricelow?: string;
      internetAddressDisplayYN?: boolean;
      mustRemovePrimaryPhoto?: boolean;
      primaryPhotoUrl?: string;
      features?: string[];
      listingStatus?: string;
      listingOffice?: string;
      mlsstatus?: string;
    }
  ): Promise<SavedPropertyResponse> {
    try {
      await remiApi.post("/idx/saved-properties", {
        property_id: propertyId,
        mls_number: mlsNumber,
        mls_board_code: mlsBoardCode,
        address,
        price: details?.price,
        list_price: details?.listprice,
        bedrooms: details?.bedrooms,
        bathrooms: details?.bathrooms,
        square_feet: details?.squareFeet,
        listing_status: details?.listingStatus,
        primary_photo_url: details?.primaryPhotoUrl,
      });

      return {
        success: true,
        property: {
          id: propertyId,
          contactId: _contactId,
          organizationId: _organizationId,
          propertyId,
          mlsNumber,
          mlsBoardCode,
          address,
          savedAt: new Date(),
          lastViewedAt: new Date(),
          ...details,
        },
      };
    } catch (error) {
      console.error("Error saving property:", error);
      return {
        success: false,
        error: "Failed to save property",
      };
    }
  },

  /**
   * Get all saved properties for the signed-in web user.
   */
  async getSavedProperties(
    contactId: string,
    organizationId: string
  ): Promise<SavedProperty[]> {
    // Dedupe concurrent full-list fetches — every property card checks its
    // saved-state on the same render, which otherwise fires N identical GETs.
    // Cleared on settle so a fetch after a save/unsave is always fresh.
    if (inFlightSavedList) return inFlightSavedList;
    inFlightSavedList = (async () => {
      try {
        const { data } = await remiApi.get<{
          saved_properties: IdxSavedProperty[];
        }>("/idx/saved-properties");
        return (data.saved_properties ?? []).map((p) =>
          toSavedProperty(p, contactId, organizationId)
        );
      } catch (error) {
        console.error("Error getting saved properties:", error);
        return [];
      } finally {
        inFlightSavedList = null;
      }
    })();
    return inFlightSavedList;
  },

  /**
   * Find a specific saved property by MLS number (used to render the
   * already-saved state on the property card / button).
   */
  async findSavedProperty(
    contactId: string,
    organizationId: string,
    mlsNumber: string
  ): Promise<SavedProperty | null> {
    try {
      const properties = await this.getSavedProperties(
        contactId,
        organizationId
      );
      return (
        properties.find(
          (p) => p.mlsNumber === mlsNumber || p.propertyId === mlsNumber
        ) ?? null
      );
    } catch (error) {
      console.error("Error finding saved property:", error);
      return null;
    }
  },

  /**
   * Remove a saved property. Keyed on the property id (the DELETE endpoint
   * resolves the contact from the Firebase token).
   */
  async removeSavedProperty(
    propertyId: string
  ): Promise<SavedPropertyResponse> {
    try {
      await remiApi.delete(
        `/idx/saved-properties/${encodeURIComponent(propertyId)}`
      );
      return {
        success: true,
      };
    } catch (error) {
      console.error("Error removing saved property:", error);
      return {
        success: false,
        error: "Failed to remove property",
      };
    }
  },
};
